// The coach's daily view, on the client.
//
// The server makes every judgement (routes/rollup.go); this decides how a
// roster reads. The organising question is "who needs chasing" — not "how is
// everyone doing", which is a report rather than a worklist.

/**
 * How an athlete's status reads.
 *
 * @returns {{text: string, tone: 'attention'|'active'|'quiet'|'idle'}}
 */
export function statusOf(row) {
  if (!row) return { text: '', tone: 'idle' };

  if (row.needsAttention) {
    return { text: row.reason || 'Needs attention', tone: 'attention' };
  }
  if (!row.programmes || row.programmes.length === 0) {
    // Not a problem, and deliberately not flagged: a coach who has not given
    // somebody work does not need telling they have not done it.
    return { text: 'No programme assigned', tone: 'idle' };
  }
  if (row.daysSince === null || row.daysSince === undefined) {
    return { text: 'Not started', tone: 'quiet' };
  }
  if (row.daysSince === 0) return { text: 'Trained today', tone: 'active' };
  if (row.daysSince === 1) return { text: 'Trained yesterday', tone: 'active' };
  return { text: `${row.daysSince} days ago`, tone: row.daysSince >= 5 ? 'quiet' : 'active' };
}

/**
 * A one-line summary of the week, or null when there is nothing to say.
 *
 * Null rather than "0 sets": an athlete with no programme has done no work and
 * is not supposed to have, and "0 sets this week" against their name reads as a
 * failing.
 */
export function weekSummary(row) {
  if (!row || !row.programmes || row.programmes.length === 0) return null;
  const sets = Number(row.setsThisWeek) || 0;
  return `${sets} set${sets === 1 ? '' : 's'} this week`;
}

/** How many need chasing — the number a coach reads first. */
export function attentionCount(rows) {
  return (rows ?? []).filter((r) => r.needsAttention).length;
}

/**
 * The heading for the whole roster.
 *
 * Says the good state explicitly. A screen that only ever shows problems gives
 * no signal when there are none, and "nothing needs your attention" is worth
 * reading.
 */
export function rosterHeadline(rows) {
  const list = rows ?? [];
  if (list.length === 0) return 'Nobody on your roster yet';

  const flagged = attentionCount(list);
  if (flagged === 0) {
    return `All ${list.length} athlete${list.length === 1 ? '' : 's'} on track`;
  }
  return `${flagged} of ${list.length} need${flagged === 1 ? 's' : ''} attention`;
}

/** What is due across the roster today. */
export function dueTodayCount(rows) {
  return (rows ?? []).reduce((total, r) => total + (Number(r.dueToday) || 0), 0);
}

/**
 * How the athlete said they arrived, for the roster row.
 *
 * # WHY ABSENCE RETURNS NULL RATHER THAN A NEUTRAL LABEL
 *
 * An athlete training three times a week has four days with nothing to say, and
 * "not checked in" printed against four of every seven names is noise that
 * teaches a coach to stop reading the column. A row simply says nothing.
 *
 * # WHY IT NAMES THE FIELD RATHER THAN SHOWING FOUR NUMBERS
 *
 * A roster is twenty rows read in one glance. "Sleep 2" is actionable at that
 * distance; "4 3 2 5" is a puzzle, and an average is a lie — 4-4-4-4 and
 * 1-4-4-7 have the same mean and describe completely different mornings.
 */
export function readinessSummary(row) {
  const readiness = row?.readiness;
  if (!readiness) return null;

  const lowest = Number(readiness.lowest) || 0;
  if (!lowest) return null;

  const field = readiness.lowestField || 'readiness';
  if (readiness.needsALook) {
    return { text: `${field} ${lowest}`, tone: 'attention' };
  }
  return { text: 'Checked in', tone: 'active' };
}
